import PropTypes from 'prop-types';
import { memo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import { fDateTime } from '../../utils/formatTime';
import { fNumber } from '../../utils/formatNumber';
import AdminStatusChip from '../admin/shared/AdminStatusChip';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

const SORT_OPTIONS = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

const SOCKET_STATUS_COLORS = {
  connected: 'success',
  connecting: 'warning',
  disconnected: 'default',
  error: 'error',
};

function CardRedemptionOrderListPane({
  filters,
  filterLoading,
  loading,
  loadingState,
  onApplyFilters,
  onClearSearch,
  onFiltersChange,
  onPageChange,
  onRefresh,
  onSearch,
  onSearchInputChange,
  onSelectOrder,
  openedOrderId,
  orders,
  pagination,
  searchInput,
  searchMode,
  searchQuery,
  selectedOrderId,
  websocketStatus,
}) {
  const [showFilters, setShowFilters] = useState(false);
  const showInitialSkeleton = loadingState === 'initial' && !orders.length;
  const showPaneBackdrop = loading && loadingState !== 'initial' && !!orders.length;
  const isRefreshLoading = loading && loadingState === 'refresh';
  const isSearchLoading = loading && loadingState === 'search';
  const backdropLabel =
    (loadingState === 'pagination' && 'Loading page') ||
    (loadingState === 'filters' && 'Applying filters') ||
    (loadingState === 'search' && 'Refreshing search') ||
    'Refreshing orders';

  return (
    <Paper
      elevation={0}
      sx={{
        height: 'calc(100vh - 180px)',
        minHeight: 640,
        maxHeight: 'calc(100vh - 180px)',
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Box sx={{ px: 2, py: 1.75 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1">Orders</Typography>
            <Typography variant="caption" color="text.secondary">
              Compact operator queue
            </Typography>
          </Box>

          <Chip
            size="small"
            color={SOCKET_STATUS_COLORS[websocketStatus] || 'default'}
            label={websocketStatus}
            icon={<Iconify icon="solar:pulse-2-bold" width={14} height={14} />}
            sx={{ textTransform: 'capitalize', fontWeight: 700, height: 26 }}
          />
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={searchInput}
              onChange={(event) => onSearchInputChange(event.target.value)}
              placeholder="Find by order ID"
              size="small"
              fullWidth
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="solar:magnifer-linear" width={16} height={16} />
                  </InputAdornment>
                ),
              }}
            />

            <LoadingButton loading={isSearchLoading} variant="contained" size="small" onClick={onSearch}>
              Find
            </LoadingButton>

            <Tooltip title="Refresh list">
              <span>
                <IconButton size="small" color="primary" onClick={onRefresh} disabled={loading}>
                  <Iconify icon="solar:restart-bold" width={18} height={18} />
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              size="small"
              color={showFilters ? 'primary' : 'default'}
              onClick={() => setShowFilters((current) => !current)}
            >
              <Iconify icon="solar:tuning-2-linear" width={18} height={18} />
            </IconButton>
          </Stack>
          {/* 
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={onOpenSelectedOrder}
              disabled={!selectedOrderId}
              startIcon={<Iconify icon="solar:eye-linear" width={16} height={16} />}
            >
              Open selected
            </Button>
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={onCloseOpenOrder}
              disabled={!openedOrderId}
              startIcon={<Iconify icon="solar:close-circle-linear" width={16} height={16} />}
            >
              Close open
            </Button>
          </Stack> */}
        </Stack>

        <Collapse in={showFilters}>
          <Stack spacing={1} sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: 'grey.100' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From"
                  value={filters.start_date}
                  onChange={(value) => onFiltersChange('start_date', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To"
                  value={filters.end_date}
                  onChange={(value) => onFiltersChange('end_date', value)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                select
                size="small"
                fullWidth
                label="Status"
                value={filters.status}
                onChange={(event) => onFiltersChange('status', event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                fullWidth
                label="Sort"
                value={filters.sort_order}
                onChange={(event) => onFiltersChange('sort_order', event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <LoadingButton loading={filterLoading} variant="contained" size="small" onClick={onApplyFilters}>
              Apply filters
            </LoadingButton>
          </Stack>
        </Collapse>
      </Box>

      {searchMode && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Search result for <strong>{searchQuery}</strong>
              </Typography>
              <Button size="small" onClick={onClearSearch}>
                Clear
              </Button>
            </Stack>
          </Box>
        </>
      )}

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Scrollbar sx={{ height: '100%' }}>
          <List disablePadding>
            {showInitialSkeleton && (
              <Box sx={{ px: 1.75, py: 1 }}>
                {[0, 1, 2, 3, 4, 5].map((item) => (
                  <Box key={item} sx={{ py: 1.15, px: 0.25 }}>
                    <Stack spacing={0.8}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Skeleton variant="text" width="42%" height={18} />
                        <Skeleton variant="rounded" width={74} height={22} />
                      </Stack>
                      <Skeleton variant="text" width="58%" height={14} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Skeleton variant="text" width="26%" height={14} />
                        <Skeleton variant="text" width="32%" height={14} />
                      </Stack>
                      <Skeleton variant="text" width="38%" height={12} />
                    </Stack>
                    <Divider sx={{ mt: 1.25 }} />
                  </Box>
                ))}
              </Box>
            )}

            {!showInitialSkeleton &&
              orders.map((order) => {
                const selected = selectedOrderId === order.order_id;
                const opened = openedOrderId === order.order_id;
                const owned = Boolean(order.staff_id);

                return (
                  <Box key={order.order_id}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => onSelectOrder(order.order_id)}
                      sx={{
                        px: 1.75,
                        py: 1.15,
                        minHeight: 86,
                        alignItems: 'flex-start',
                        borderLeft: '3px solid transparent',
                        '&.Mui-selected': {
                          bgcolor: 'success.lighter',
                          borderLeftColor: 'success.main',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack spacing={0.6}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box sx={{ minWidth: 0, pr: 0.5 }}>
                                <Typography variant="subtitle2" noWrap sx={{ fontSize: 13 }}>
                                  {order.first_name} {order.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                                  {order.category}
                                </Typography>
                              </Box>

                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {opened && (
                                  <Tooltip title="Open in workspace">
                                    <Box sx={{ color: 'primary.main', display: 'flex' }}>
                                      <Iconify icon="solar:eye-bold" width={14} height={14} />
                                    </Box>
                                  </Tooltip>
                                )}
                                {owned && (
                                  <Tooltip
                                    title={order.staff_name ? `Owned by ${order.staff_name}` : 'Order has an owner'}
                                  >
                                    <Box sx={{ color: 'success.main', display: 'flex' }}>
                                      <Iconify icon="solar:user-check-bold" width={14} height={14} />
                                    </Box>
                                  </Tooltip>
                                )}
                                <AdminStatusChip status={order.status || 'pending'} size="small" />
                              </Stack>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11.5 }}>
                                {order.currency} {fNumber(order.amount)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{ fontSize: 11.5 }}
                              >
                                {order.settlement_currency || 'NGN'} {fNumber(order.settlement_amount)}
                              </Typography>
                            </Stack>

                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
                              {fDateTime(order.created_at)}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItemButton>
                    <Divider />
                  </Box>
                );
              })}

            {!showInitialSkeleton && !orders.length && (
              <Box sx={{ px: 2, py: 5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  No orders available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust your filters or wait for new card redemption activity.
                </Typography>
              </Box>
            )}
          </List>
        </Scrollbar>

        <Backdrop
          open={showPaneBackdrop}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            color: 'common.white',
            bgcolor: 'rgba(15, 23, 42, 0.22)',
          }}
        >
          <Stack spacing={1.25} alignItems="center">
            <Iconify
              icon={isRefreshLoading ? 'solar:restart-bold' : 'solar:hourglass-line-duotone'}
              width={24}
              height={24}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
              {backdropLabel}
            </Typography>
          </Stack>
        </Backdrop>
      </Box>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Pagination
          color="primary"
          page={pagination.current_page || 1}
          count={pagination.total_pages || 1}
          onChange={(_event, page) => onPageChange(page)}
          disabled={loading}
          size="small"
          siblingCount={0}
          boundaryCount={1}
        />
      </Box>
    </Paper>
  );
}

export default memo(CardRedemptionOrderListPane);

CardRedemptionOrderListPane.propTypes = {
  filters: PropTypes.shape({
    end_date: PropTypes.any,
    sort_order: PropTypes.string,
    start_date: PropTypes.any,
    status: PropTypes.string,
  }).isRequired,
  filterLoading: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  loadingState: PropTypes.string.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSelectOrder: PropTypes.func.isRequired,
  openedOrderId: PropTypes.string,
  orders: PropTypes.array.isRequired,
  pagination: PropTypes.object.isRequired,
  searchInput: PropTypes.string.isRequired,
  searchMode: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string.isRequired,
  selectedOrderId: PropTypes.string,
  websocketStatus: PropTypes.string.isRequired,
};
